import { userAtom } from '@/services/t4account/user';
import { getGoogleAnalyticsId, isEnableAuth } from '@/util/appConfig';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMount, usePrevious } from 'react-use';
import { useSetRecoilState } from 'recoil';
import ReactGA from 'react-ga4';
import { authenticationManager } from '@/util/authenticationManager';

export const useAppRouter = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useSetRecoilState(userAtom);
  const [isLoading, setIsLoading] = useState(isEnableAuth);
  const googleAnalyticsId = useRef(getGoogleAnalyticsId());

  const pathLang = useMemo(
    () => (location.pathname.split('/')[1] === 'en' ? 'en' : 'ja'),
    [location.pathname],
  );
  const prevPathLang = usePrevious(pathLang);

  useMount(() => {
    // Google Analytics
    if (googleAnalyticsId.current) {
      ReactGA.initialize(googleAnalyticsId.current);
    }
  });

  useEffect(() => {
    // Google Analytics
    if (googleAnalyticsId.current) {
      ReactGA.send({
        hitType: 'pageview',
        page: `${location.pathname}${location.search}${location.hash}`,
      });
    }
  }, [location]);

  useEffect(() => {
    if (prevPathLang && pathLang !== prevPathLang) {
      i18n.changeLanguage(pathLang);
    }
    // eslint-disable-next-line
  }, [pathLang, prevPathLang]);

  useEffect(() => {
    document.body.setAttribute('data-lang', i18n.language);
    const { pathname, search, hash } = window.location;
    const langPath = window.location.pathname.split('/')[1];

    // to EN
    if (i18n.language === 'en') {
      if (langPath === 'en') return;
      navigate(`/en${pathname}${search}${hash}`);
      return;
    }
    // to JA
    if (i18n.language === 'ja') {
      if (langPath !== 'en') return;
      navigate(`${pathname.replace('/en/', '/')}${search}${hash}`);
      return;
    }
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    if (!isEnableAuth) return;
    (async () => {
      let u = await authenticationManager.userManager.getUser();
      if (!u) {
        try {
          u = await authenticationManager.userManager.signInSilent();
        } catch (err) {
          console.log(err);
        }
      }
      setUser(u);
      setIsLoading(false);
    })();
  }, [setUser]);

  useMount(() => {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.id = 'Cookiebot';
    scriptElement.dataset['culture'] = i18n.language;
    scriptElement.dataset['cbid'] = import.meta.env.VITE_CB_ID;
    scriptElement.dataset['blockingmode'] = 'auto';
    scriptElement.src = 'https://consent.cookiebot.com/uc.js';
    const headElement = document.getElementsByTagName('head');
    if (!headElement[0]) return;
    headElement[0].appendChild(scriptElement);
  });

  return {
    isLoading,
  };
};
